import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { PatientService } from 'src/app/core/api/patient.service';
import { SpecialValueKey } from 'src/app/form-template/form-template-model';
import { LocationSelectService } from '../location-select/location-select.service';
import { HealthCardMode, LocationHealthCardModeMap } from '../location-select/location.model';
import { PatientEntryMethod, PatientFormGroup } from './patient-form.model';

@Component({
  selector: 'csi-patient-form',
  templateUrl: './patient-form.component.html',
  styleUrls: ['./patient-form.component.css']
})
export class PatientFormComponent implements OnInit {
  @Input() intakeView: boolean = false;
  @Input() showResearchConsent = false;
  @Input() canLookupPatients = false;
  @Input() hideTitle = false;
  @Input() showInCard = true;
  @Input() country: string;
  @Input() formatForPDF = false;
  @Input() requireAtLeastOneCommunication = false;
  @Input() isPatientView = true;

  public pharmacyNameSpecialOption = {
    [SpecialValueKey.NONE]: {
      label: 'No pharmacy'
    }
  };

  public pharmacyPhoneNumberAndCountryCodeSpecialOption = {
    [SpecialValueKey.NONE]: {
      label: 'No pharmacy number'
    }
  };

  private _formGroup: PatientFormGroup;
  @Input() set formGroup(formGroup: PatientFormGroup) {
    const formGroupDisabled = formGroup.disabled;

    const phoneNumberAndCountryCodeFormGroup = PatientFormGroup.getPhoneNumberAndCountryCodeFormGroup();
    if (!formGroup.controls.phoneNumberAndCountryCode)
      formGroup.addControl('phoneNumberAndCountryCode', phoneNumberAndCountryCodeFormGroup);

    phoneNumberAndCountryCodeFormGroup.patchValue(
      PatientFormGroup.getCountryCodeAndPhoneNumberFromPhoneNumber(formGroup.controls.phone.value)
    );

    PatientFormGroup.syncPhoneNumberWithPhoneNumberAndCountryCodeFormGroup(
      formGroup.controls.phoneNumberAndCountryCode,
      formGroup.controls.phone
    );

    if (formGroupDisabled) formGroup.disable();

    this._formGroup = formGroup as PatientFormGroup;
  }
  get formGroup() {
    return this._formGroup;
  }

  public LocationHealthCardModeMap = LocationHealthCardModeMap;
  public healthCardConfig$: Observable<
    HealthCardMode
  > = this.locationSelectService.getHealthCardConfigForClinic();
  public countryCodes: any = {};
  public readonly HealthCardMode = HealthCardMode;
  public readonly PatientEntryMethod = PatientEntryMethod;
  private readonly IntakeViews = 'intake-forms';

  @ViewChild('patientLookupLabel', { static: false }) patientLookupLabel: ElementRef;

  constructor(
    private locationSelectService: LocationSelectService,
    private changeRef: ChangeDetectorRef,
    private patientService: PatientService
  ) {}
  ngOnInit() {
    if (!this.isPatientView) {
      this.pharmacyNameSpecialOption = null;
      this.pharmacyPhoneNumberAndCountryCodeSpecialOption = null;
    }
    combineLatest([
      this.formGroup.controls.firstName.valueChanges.pipe(startWith(true)),
      this.formGroup.controls.lastName.valueChanges.pipe(startWith(true))
    ]).subscribe(() => {
      this.formGroup.controls.firstName.setValue(
        (this.formGroup.controls.firstName.value || ' ').trimStart(),
        {
          onlySelf: true,
          emitEvent: false
        }
      );
      this.formGroup.controls.lastName.setValue(
        (this.formGroup.controls.lastName.value || ' ').trimStart(),
        { onlySelf: true, emitEvent: false }
      );
    });

    this.formGroup.controls.gender.valueChanges.subscribe(val => {
      val === 'other'
        ? this.formGroup.controls.genderOther.enable()
        : this.formGroup.controls.genderOther.disable();
      this.changeRef.detectChanges();
    });

    if (!this.formGroup.controls.gender.value) {
      this.formGroup.controls.genderOther.disable();
    }

    if (this.requireAtLeastOneCommunication) {
      this.formGroup.setValidators(this.patientService.contactValidator);
      this.formGroup.updateValueAndValidity();
    }
  }

  public passFocusToLookupInput(event?: FocusEvent | TouchEvent) {
    this.formGroup.controls.entryMethod.setValue(PatientEntryMethod.LookupExisting);

    const inputBox = this.patientLookupLabel.nativeElement.querySelector('input') as HTMLElement;
    if (inputBox) {
      if (event) {
        event.preventDefault();
      }
      inputBox.focus();
    }
  }

  get canShowForm(): boolean {
    return (
      !this.formGroup.controls.entryMethod.value ||
      this.formGroup.controls.entryMethod.value === PatientEntryMethod.AddNewPatient ||
      this.formGroup.controls.id.value
    );
  }
}
